import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../core/services';
import { Provider } from '../../features/provider/models';
import { PendingProviderSelectionService } from './pending-provider-selection.service';

@Injectable({
    providedIn: 'root'
})
export class ProviderLinkService {
    providerId: number | undefined;
    programIds: number[] | undefined;
    provider$: Observable<Provider | undefined> | undefined;

    constructor(
        private readonly _api: ApiService,
        private readonly _router: Router,
        private readonly _pendingSelection: PendingProviderSelectionService
    ) {}

    get hasProvider(): boolean {
        return (
            this.providerId !== null &&
            this.providerId !== undefined &&
            this.providerId > 0
        );
    }

    /**
     * Drops the in-memory deep-link state.
     *
     * This service is `providedIn: 'root'` and nothing used to clear these fields, so a tab
     * that had ever loaded `/{client}/providers/{id}` kept that id for the rest of its life.
     * Loading a second client's portal over the first therefore left `hasProvider` true with
     * an id belonging to the *previous* client, and `signOut()` would navigate to
     * `/{newClient}/providers/{oldClientsProviderId}` (LA-22939).
     *
     * Deliberately NOT called on sign-out: landing a signed-out student back on their
     * provider's page is intended behaviour, and it needs `providerId` to still be here.
     */
    reset(): void {
        this.providerId = undefined;
        this.programIds = undefined;
        this.provider$ = undefined;
    }

    getProviderIdWithFallback(): number | undefined {
        if (this.hasProvider && this.providerId) {
            return this.providerId;
        }
        return this._pendingSelection.providerIds[0];
    }

    get hasPrograms(): boolean {
        return (
            this.programIds !== null &&
            this.programIds !== undefined &&
            this.programIds.length > 0 &&
            this.programIds.every((id) => id > 0)
        );
    }

    checkForParams(params: ParamMap) {
        const providerId = params.get('providerId');
        this.providerId =
            providerId !== null && providerId !== undefined && +providerId > 0
                ? +providerId
                : undefined;

        this.programIds = this.parseProgramIds(params);

        this.updateSessionStorage();
        this.captureSource();
        this.getProvider();
    }

    /**
     * Record which portal pathway the student entered through (LA-22626).
     * Set whenever a provider link is processed. If the student navigates between statewide and agency portal,
     * it updates to reflect the current pathway (which deep link they're currently viewing).
     * Entering through an agency-specific provider link => 'Agency Portal', otherwise the statewide link => 'Statewide Portal'.
     * Sent to the API on the first application update and persisted on the application.
     */
    private captureSource(): void {
        const source = this.hasProvider ? 'Agency Portal' : 'Statewide Portal';
        this._pendingSelection.setSource(source);
    }

    cleanProgramIds(provider: Provider | undefined): void {
        if (
            this.programIds === null ||
            this.programIds === undefined ||
            this.programIds.length <= 0
        ) {
            this.programIds = undefined;
            this.updateSessionStorage();
            this._router.navigate([
                `${runtimeConfig.clientCode}/providers/${this.providerId}`
            ]);
            return;
        }

        const realIds = provider?.programs.map((p) => p.programId);
        const routeIds = this.programIds;

        if (
            realIds === null ||
            realIds === undefined ||
            realIds.length <= 0 ||
            routeIds === null ||
            routeIds === undefined ||
            routeIds.length <= 0
        ) {
            this.programIds = undefined;
            this.updateSessionStorage();
            this._router.navigate([
                `${runtimeConfig.clientCode}/providers/${this.providerId}`
            ]);
            return;
        }

        if (realIds!.sort() != routeIds!.sort()) {
            const onlyRealIds = routeIds!.filter((id) => realIds?.includes(id));
            this.programIds = onlyRealIds;
            this.updateSessionStorage();
            this._router.navigate([
                `${runtimeConfig.clientCode}/providers/${this.providerId}/programs/${onlyRealIds}`
            ]);
        }
    }

    private updateSessionStorage(): void {
        this._pendingSelection.setProgramIds(
            this.hasPrograms ? <number[]>this.programIds : []
        );
        this._pendingSelection.setProviderIds(
            this.hasProvider ? [<number>this.providerId] : []
        );
    }

    private getProvider(): void {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('clientId', runtimeConfig.clientId);
        httpParams = httpParams.append('providerIds', <number>this.providerId);
        if (this.hasPrograms) {
            this.programIds?.forEach(
                (id) => (httpParams = httpParams.append('programIds', id))
            );
        }
        this.provider$ = this._api
            .get('/providers', httpParams)
            .pipe(
                map((result) =>
                    result?.providers?.length > 0
                        ? result.providers[0]
                        : undefined
                )
            );
    }

    private parseProgramIds(params: ParamMap): number[] | undefined {
        const programIds = params.getAll('programIds');
        if (programIds.length > 1) {
            return programIds.map((id) => +id).filter(Number);
        } else if (programIds.length === 1) {
            return programIds[0]
                .split(',')
                .map((id) => +id)
                .filter(Number);
        } else {
            return undefined;
        }
    }
}
